import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { get, groupBy } from 'lodash';
import Alert from '@lendi-ui/alert';

import {
  EMPLOYMENT_TYPE,
  FIELD_PATHS,
  FIELD_VALUES,
  READONLY_ALERT_BODY,
  READONLY_ALERT_HEADING,
  REDUX_STORE,
  URL_PARAM_APPLICATION_ID,
} from '@funnels/constants';
import { useActiveRolesFromStore, useCustomerFormApplicant, useFormValues } from '@funnels/hooks';
import { StoreApplicant, StoreEmployment, UseParams } from '@funnels/types';
import { BoldHeading, FunnelGridUnit } from '@funnels/ui-components';
import { AccordionGroup } from '@funnels/ui-components/Common';
import { Card } from '@funnels/ui-layouts/customer-view/_common';
import { getApplicantName, getBrandSpecificBaseURL, getIndexFromId } from '@funnels/utils';

import { EmploymentSummaryAccordion } from '../../Layout/EmploymentSummaryAccordion';

interface EmploymentSummaryProps {
  targetApplicantRole: FIELD_VALUES.BORROWER | FIELD_VALUES.GUARANTOR;
}

export const EmploymentSummary = ({ targetApplicantRole }: EmploymentSummaryProps) => {
  const [formValues] = useFormValues();
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = useParams<UseParams>();
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const customerApplicant = useCustomerFormApplicant();
  const roles = useActiveRolesFromStore();
  const hasReadonly = Array.isArray(roles) && roles.includes(REDUX_STORE.ACTIVE_ROLES.ACTIVE_ROLES_VALUES.APP_READONLY);
  const customerRole = get(
    { applicants: [customerApplicant] },
    FIELD_PATHS.APPLICANT_TYPE(0),
    FIELD_VALUES.BORROWER as FIELD_VALUES.GUARANTOR | FIELD_VALUES.BORROWER
  );
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  employments.sort((a, b) => Number(b.type! === EMPLOYMENT_TYPE.PRIMARY) - Number(a.type! === EMPLOYMENT_TYPE.PRIMARY));
  const filteredApplicants = applicants.filter(
    (applicant) => get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0)) === targetApplicantRole
  );
  const applicantsEmployments = groupBy(employments, 'applicantId');

  const heading = `${
    filteredApplicants.length > 1
      ? `${targetApplicantRole === FIELD_VALUES.BORROWER ? `Applicants'` : `Guarantors'`}`
      : customerRole === targetApplicantRole
      ? 'Your'
      : `${get({ applicants: filteredApplicants }, FIELD_PATHS.APPLICANT_FIRST_NAME(0))}'s`
  } employment history`;

  // to ensure in the situation where the application is not locked,
  // we still need to check the roles and permission for the display logic of Edit button.
  const showEditButton =
    !hasReadonly && !(customerRole === FIELD_VALUES.GUARANTOR && targetApplicantRole === FIELD_VALUES.BORROWER);

  const onClickEditButton = useCallback(
    (applicantId: string) => () => {
      const editPageUrl = `${getBrandSpecificBaseURL()}/application/${applicationId}/applicant/${applicantId}/employment-summary-edit/`;
      window.location.assign(editPageUrl);
    },
    [applicationId]
  );

  return (
    <Card heading={heading} actionCustomText="Done">
      {hasReadonly && (
        <FunnelGridUnit mb="sm" mt="sm" data-component="readonly-alert">
          <Alert variant="info" heading={READONLY_ALERT_HEADING}>
            {READONLY_ALERT_BODY}
          </Alert>
        </FunnelGridUnit>
      )}
      {
        // Do not allow the borrower to view the guarantors' employment summary,
        // the backend will also implement access control
        (customerRole !== FIELD_VALUES.BORROWER || customerRole === targetApplicantRole) &&
          filteredApplicants.map((applicant) => {
            if (!applicantsEmployments[applicant.id!]) return null;
            return (
              <AccordionGroup key={applicant.id}>
                {filteredApplicants.length > 1 && (
                  <BoldHeading size="xs" align="center" my="md">{`${getApplicantName(
                    applicants,
                    applicant.id
                  )}'s employment history`}</BoldHeading>
                )}
                {!!applicantsEmployments[applicant.id!] &&
                  applicantsEmployments[applicant.id!].map(({ id }) => (
                    <EmploymentSummaryAccordion
                      key={id}
                      employmentIndex={getIndexFromId(employments, id)}
                      onClickEditButton={showEditButton ? onClickEditButton(applicant.id!) : undefined}
                    />
                  ))}
              </AccordionGroup>
            );
          })
      }
    </Card>
  );
};
